import type { TextFieldProps } from '@mui/material';
import { ButtonBase, Checkbox, InputAdornment, TextField, Typography } from '@mui/material';
import { accountsSelectors } from 'app-components/flux/accounts';
import { customersSelectors } from 'app-components/flux/customers';
import { projectsSelectors } from 'app-components/flux/projects';
import { transactionsSelectors } from 'app-components/flux/transactions';
import classNames from 'classnames';
import CustomerAndProjectSelector from 'components/CustomerAndProjectSelector';
import { InvoicePaymentUpsertAction } from 'components/InvoicePayment/actions';

import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import { categoriesSelectors } from 'app-components/flux/categories';
import type { ChartOfAccountProps } from 'app-components/flux/chart-of-accounts/chartOfAccountsTypes';
import type {
  LinkedItem,
  LinkedItemProps,
  SplitItem,
  SplitItemProps,
  TransactionProps,
  TransactionRecord,
  TransactionType,
} from 'app-components/flux/transactions/transactionsTypes';
import { useOpenInvoice } from 'containers/SalesAndBilling/invoiceHooks';
import { useAppSelector } from 'hooks/redux';
import React, { useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { makeStyles } from 'tss-react/mui';
import { getTxnTransferHasAllocations } from 'components/Dialogs/TransactionEditDialog/selectors';

export type ProjectSelect = {
  customerId: string | null;
  projectId: string | null;
  billable?: boolean | null | undefined;
  payment?: boolean | null | undefined;
};

const useStyles = makeStyles()(() => ({
  container: {
    display: 'flex',
    alignItems: 'flex-start',
    width: '100%',
  },
}));

type OrnamentTypeProps = {
  transaction?: TransactionProps | TransactionRecord | null;
  coa?: ChartOfAccountProps | null;
  linkedItem?: LinkedItem | LinkedItemProps | null;
  type?: TransactionType | null;
  amount?: number;
  businessId?: string | null;
  split?: SplitItem | SplitItemProps | null;
  create?: boolean;
};

export type OrnamentType = 'billable' | 'payment' | 'invoice-button' | 'payment-button' | 'edit-refund-button' | 'none';

export const getOrnamentType = ({
  isIncome,
  isExpense,
  isUncategorized,
  isAssetPayment,
  showPaymentDialogLink,
  isLinkedExpense,
  amount,
  businessId,
}: {
  isIncome: boolean;
  isExpense: boolean;
  isUncategorized: boolean;
  isAssetPayment: boolean;
  showPaymentDialogLink: boolean;
  isLinkedExpense: boolean;
  amount?: number;
  businessId?: string | null;
}): OrnamentType => {
  if (isLinkedExpense) {
    return 'invoice-button';
  }

  if (showPaymentDialogLink && amount && amount > 0) {
    return 'payment-button';
  }

  if (showPaymentDialogLink && amount && amount < 0) {
    return 'edit-refund-button';
  }

  if ((isIncome || ((amount ?? 0) > 0 && isUncategorized)) && businessId) {
    return 'payment';
  }

  if (isAssetPayment) {
    return 'payment';
  }

  if ((isExpense || ((amount ?? 0) < 0 && isUncategorized)) && businessId) {
    return 'billable';
  }

  return 'none';
};

export const useOrnamentType = (props: OrnamentTypeProps): OrnamentType => {
  const { transaction, coa, linkedItem, amount, businessId, split, create = false } = props;
  const incomeCOAs = useAppSelector(categoriesSelectors.getIncomeCOAIds);
  const isIncome = incomeCOAs.includes(coa?.id ?? '');
  const isExpense = useAppSelector((state) =>
    coa?.type === 'CATEGORY' ? Boolean(categoriesSelectors.isExpenseCat(state, coa.id)) : false,
  );
  const arAccountIds = useAppSelector(accountsSelectors.getAllArAccountIds);
  const isUncategorized = !coa || coa.type === 'UNCATEGORIZED';
  const isAssetPayment = useMemo(() => {
    return arAccountIds.includes(coa?.id);
  }, [arAccountIds, coa]);
  // for checking if payment has allocations
  const showPaymentDialogLink = useAppSelector((state) => {
    return (
      !create &&
      isAssetPayment &&
      getTxnTransferHasAllocations(state, { transferId: split?.transfer?.id || transaction?.transfer?.id })
    );
  });
  const isRefund = transaction?.subtype === 'REFUND' || transaction?.subtype === 'REFUND_SHADOW_TXN';
  const isLinkedExpense = !isRefund && linkedItem?.id;

  return getOrnamentType({
    isIncome,
    isExpense,
    isUncategorized,
    isAssetPayment,
    showPaymentDialogLink,
    isLinkedExpense: Boolean(isLinkedExpense),
    amount,
    businessId,
  });
};

const Ornament = (props: any) => {
  const { invoiceId, paymentAssetId, splitId, onButtonClick, ornamentType, value, onChange, disabled } = props;

  const dispatch = useDispatch();

  const handleOpenInvoice = useOpenInvoice('details-dialog');
  const invoice = useAppSelector((state) => transactionsSelectors.getTransactionById(state, invoiceId ?? ''));

  const handleButtonClick = () => {
    onButtonClick?.();
    if (ornamentType === 'invoice-button') {
      handleOpenInvoice({ invoiceId, initialView: invoice?.sentOn ? 'Preview' : 'Edit' });
    } else {
      dispatch(
        InvoicePaymentUpsertAction({
          action: 'EDIT_ALLOCATIONS',
          context: 'details-dialog',
          initId: paymentAssetId ?? null,
          initSplitItemId: splitId ?? null,
          initScreen: 'MAIN',
        }),
      );
    }
  };

  if (ornamentType === 'billable' || ornamentType === 'payment') {
    return (
      <InputAdornment position='end'>
        <Checkbox
          checked={ornamentType === 'billable' ? value?.billable : value.payment}
          onChange={() => onChange?.({ ...value, [ornamentType]: !value?.[ornamentType] })}
          color='primary'
          disabled={disabled}
          sx={disabled ? { color: 'text.disabled' } : {}}
        />
        <Typography
          variant='paragraphRegular'
          color='textSecondary'
          onClick={() => !disabled && onChange?.({ ...value, [ornamentType]: !value?.[ornamentType] })}
          sx={disabled ? { color: 'text.disabled' } : {}}
        >
          {ornamentType === 'billable' ? 'Billable' : 'Invoice payment'}
        </Typography>
      </InputAdornment>
    );
  }

  const buttonText =
    {
      'invoice-button': 'View Invoice',
      'edit-refund-button': 'Edit Refund Assignment',
      billable: 'Billable',
      none: '',
      payment: 'Invoice payment',
      'payment-button': 'Edit Payment',
    }[ornamentType as OrnamentType] || 'Edit Payment';

  return (
    <ButtonBase
      sx={{ color: 'primary.main' }}
      onClick={handleButtonClick}
      id={ornamentType === 'invoice-button' ? 'view-invoice' : 'edit-payment-assignment'}
    >
      <PaymentsOutlinedIcon />
      <Typography variant='button' sx={{ whiteSpace: 'nowrap', textTransform: 'none', pl: 1 }}>
        {buttonText}
      </Typography>
    </ButtonBase>
  );
};

type Props = {
  value: ProjectSelect;
  className?: string;
  onChange?: (value: ProjectSelect) => void;
  register?: boolean;
  id?: string;
  ornamentType: OrnamentType;
  disabledInvoicePayment?: boolean;
  CustomOrnament?: React.ReactNode;
  disabledSelection?: boolean;
  invoiceId?: string | null;
  paymentAssetId?: string | null;
  splitId?: string;
  onButtonClick?: () => void;
  textFieldProps?: TextFieldProps;
  /** `small` uses 48px row height (e.g. Split dialog); default matches prior 58px filled row */
  size?: 'small' | 'medium';
};

export default function ClientProjectSelect(props: Props) {
  const {
    value,
    onChange,
    register = false,
    id,
    className,
    ornamentType,
    disabledInvoicePayment = false,
    disabledSelection,
    invoiceId,
    paymentAssetId,
    onButtonClick,
    splitId,
    textFieldProps,
    size = 'medium',
  } = props;

  const { classes } = useStyles();
  const filledRowHeight = register ? '28px' : size === 'small' ? '48px' : '58px';

  const customerMap = useAppSelector(customersSelectors.getCustomers);
  const projectMap = useAppSelector(projectsSelectors.getProjects);

  // TODO: need to disable if personal transaction and ornament type is payment
  const disabledBillable = !value?.customerId || disabledSelection;
  const disabled =
    ornamentType === 'none' ? false : ornamentType === 'billable' ? disabledBillable : disabledInvoicePayment;
  const showOrnament = !register && ornamentType !== 'none';

  // An invoice payment can only be saved once a client is chosen, so surface the requirement
  // on the selector itself instead of silently disabling the save button.
  const requiresClient = showOrnament && ornamentType === 'payment' && Boolean(value?.payment);
  const showRequiredError = requiresClient && !value?.customerId;

  return (
    <div className={classNames(classes.container, className)}>
      <CustomerAndProjectSelector
        disableAddCustomer
        disabled={disabledSelection}
        textFieldProps={
          {
            size: register ? undefined : size,
            InputProps: {
              sx: {
                borderTopRightRadius: showOrnament ? 0 : undefined,
                borderBottomRightRadius: showOrnament ? 0 : undefined,
                height: filledRowHeight,
                '& .Mui-focused': { boxShadow: 'none' },
                fontSize: register ? 14 : undefined,
                marginLeft: register ? 0 : undefined,
                paddingBottom: register ? '0px !important' : undefined,
              },
              disableUnderline: register,
            },
            placeholder: 'Make a selection',
            label: register ? null : 'Clients & Projects',
            variant: register ? 'standard' : 'filled',
            sx: {
              borderRightStyle: 'none',
              // The input keeps its fixed row height via InputProps; pinning the root too would
              // clip the helper text, which renders below it.
              height: showRequiredError ? undefined : filledRowHeight,
            },
            ...textFieldProps,
            error: showRequiredError || textFieldProps?.error,
            helperText: showRequiredError ? 'Required' : textFieldProps?.helperText,
          } as TextFieldProps
        }
        value={{
          customer: customerMap?.get(value?.customerId ?? '') ?? null,
          project: projectMap?.get(value?.projectId ?? '') ?? null,
        }}
        onChange={(_event, change) =>
          onChange?.({
            customerId: change?.customer?.id ?? '',
            projectId: change?.project?.id ?? '',
            billable: !!(change.customer || change.project) && ornamentType === 'billable',
            payment: value?.payment,
          })
        }
        id={id ?? 'client-project-select'}
        className={classes.container}
      />
      {!register && ornamentType !== 'none' && (
        <TextField
          disabled={disabled}
          variant='filled'
          size={size}
          InputProps={{
            sx: {
              borderTopLeftRadius: '0px',
              borderBottomLeftRadius: '0px',
              height: filledRowHeight,
            },
            startAdornment: (
              <Ornament
                invoiceId={invoiceId}
                paymentAssetId={paymentAssetId}
                splitId={splitId}
                onButtonClick={onButtonClick}
                ornamentType={ornamentType}
                value={value}
                onChange={onChange}
                disabled={disabled}
                showButton
              />
            ),
            readOnly: true,
          }}
        />
      )}
    </div>
  );
}
